/**
 * Utility functions for transforming trending topics to news items
 */

/**
 * Transform trending topic to news item structure
 * @param {Object} topic - The trending topic object from API
 * @returns {Object} Transformed news item object
 */
export const transformTopicToNewsItem = (topic) => {
  return {
    id: topic.id,
    category: topic.category || 'General',
    title: topic.topic, // Backend uses 'topic' field for the title
    description: topic.description,
    source:
      topic.sources && topic.sources.length > 0
        ? extractSourceName(topic.sources[0])
        : 'Breaking News',
    sourceUrl: topic.sources && topic.sources.length > 0 ? topic.sources[0] : '#',
    timeAgo: formatTimeAgo(topic.createdAt),
    timestamp: topic.createdAt,
    aiConfidence: generateAIConfidence(topic),
    relatedTwygsCount: Math.floor(Math.random() * 15) + 1,
    tags: generateTags(topic),
    viewpoints: topic.viewpoints || [],
    urgencyIndicator: topic.urgencyIndicator,
    readerDemand: topic.readerDemand,
    contentGap: topic.contentGap,
    numberRelatedArticle: topic.numberRelatedArticle
  };
};

/**
 * Extract source name from URL
 * @param {string} url - The source URL
 * @returns {string} Formatted source name
 */
export const extractSourceName = (url) => {
  try {
    const domain = new URL(url).hostname;
    // Remove www. and get the main domain name
    const cleanDomain = domain.replace('www.', '').split('.')[0];
    // Capitalize and handle known sources
    const knownSources = {
      cnn: 'CNN',
      bbc: 'BBC',
      nytimes: 'NY Times',
      washingtonpost: 'Washington Post',
      reuters: 'Reuters',
      ap: 'Associated Press',
      bloomberg: 'Bloomberg',
      wsj: 'Wall Street Journal',
      theguardian: 'The Guardian',
      techcrunch: 'TechCrunch',
      wired: 'Wired',
      politico: 'Politico',
      npr: 'NPR'
    };
    return (
      knownSources[cleanDomain.toLowerCase()] || cleanDomain.replace(/^\w/, (c) => c.toUpperCase())
    );
  } catch {
    return 'News Source';
  }
};

export const formatTimeAgo = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date; // difference in milliseconds
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return 'Just now';
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;

  const days = Math.floor(diffHours / 24);
  if (days === 1) return '1 day ago';
  if (days <= 30) return `${days} days ago`;

  const years = Math.floor(days / 365);
  return years <= 1 ? '1 year ago' : `${years} years ago`;
};

/**
 * Generate AI confidence score based on topic data quality
 * @param {Object} topic - The trending topic object
 * @returns {number} Confidence score (0-95)
 */
export const generateAIConfidence = (topic) => {
  // Base confidence on data quality indicators
  let confidence = 70; // Base confidence

  // Higher confidence for topics with rich data
  if (topic.sources && topic.sources.length > 0) confidence += 15;
  if (topic.viewpoints && topic.viewpoints.length > 0) confidence += 10;
  if (topic.sources && topic.sources.length > 1) confidence += 5;
  if (topic.description && topic.description.length > 100) confidence += 5;
  if (topic.viewpoints && topic.viewpoints.length > 2) confidence += 5;

  return Math.min(confidence, 95); // Cap at 95%
};

/**
 * Generate tags from topic title and description
 * @param {Object} topic - The trending topic object
 * @returns {Array<string>} Array of tags
 */
export const generateTags = (topic) => {
  // Extract meaningful tags from title and description
  const commonWords = [
    'the',
    'and',
    'or',
    'but',
    'in',
    'on',
    'at',
    'to',
    'for',
    'of',
    'with',
    'by',
    'a',
    'an',
    'is',
    'are',
    'was',
    'were',
    'be',
    'been',
    'being',
    'have',
    'has',
    'had',
    'do',
    'does',
    'did',
    'will',
    'would',
    'could',
    'should',
    'this',
    'that',
    'these',
    'those',
    'from',
    'into',
    'about',
    'over',
    'under',
    'up',
    'down',
    'out',
    'off',
    'on',
    'over',
    'under',
    'again',
    'further',
    'then',
    'once'
  ];

  const text = `${topic.topic} ${topic.description}`.toLowerCase(); // Use topic.topic for title
  const words = text.match(/\b\w{4,}\b/g) || []; // Get words with 4+ characters

  const meaningfulWords = [...new Set(words)]
    .filter((word) => !commonWords.includes(word))
    .filter((word) => !/^\d+$/.test(word)) // Remove pure numbers
    .slice(0, 4); // Limit to 4 tags

  // Add category as a tag if not already included
  const categoryTag = topic.category?.toLowerCase();
  if (categoryTag && categoryTag !== 'general' && !meaningfulWords.includes(categoryTag)) {
    meaningfulWords.unshift(categoryTag);
  }

  return meaningfulWords.slice(0, 4); // Ensure max 4 tags
};
